// External dependencies
import classnames from 'classnames';
import React from 'react';

// Internal dependencies
import { isBlogCategoriesEnabled } from '@/modules/cms/client/utils/feature-flags';

/**
 * Secondary navigation for the CMS admin screens.
 *
 * Rendered below `AdminHeader` on every CMS screen.
 *
 * `prefix` marks an item active for its child screens too: the page editor
 * lives under `/admin/cms/pages/...` and belongs to "Pages", the post editor
 * under `/admin/cms/posts/...` belongs to "Blog".
 */
const NAV_ITEMS = [
  { path: '/admin/cms', label: 'Pages', prefix: '/admin/cms/pages' },
  { path: '/admin/cms/posts', label: 'Blog', prefix: '/admin/cms/posts' },
  // Hidden while `featureFlags.blogCategories` is off; see `visibleItems()`.
  {
    path: '/admin/cms/blog-categories',
    label: 'Blog categories',
    enabled: isBlogCategoriesEnabled,
  },
  { path: '/admin/cms/faq', label: 'FAQ' },
  { path: '/admin/cms/trash', label: 'Trash' },
  { path: '/admin/cms/announcements', label: 'Announcements' },
  // "Header menu" is the page-in-header flag screen; "Footer menu" is the
  // section-based footer manager. Two distinct menus, two managers.
  { path: '/admin/cms/menus', label: 'Header menu' },
  { path: '/admin/cms/footer-menu', label: 'Footer menu' },
  { path: '/admin/cms/settings', label: 'Settings' },
];

/**
 * Is this nav item the active one for the current path?
 *
 * Paths are matched exactly, plus the item's `prefix` for its child screens.
 * A general prefix match would light up "Pages" on every CMS screen.
 *
 * @param {Object} item - Nav item
 * @param {String} currentPath - `window.location.pathname`
 * @returns {Boolean}
 */
function isActive(item, currentPath) {
  const path = currentPath.replace(/\/$/, '') || '/admin/cms';

  if (path === item.path) {
    return true;
  }

  return Boolean(item.prefix) && path.startsWith(`${item.prefix}/`);
}

export default function CmsAdminNav() {
  const currentPath = window.location.pathname;

  return (
    <nav className="cms-admin-nav" aria-label="CMS sections">
      <div className="container">
        <ul className="nav nav-pills">
          {NAV_ITEMS.filter(item => !item.enabled || item.enabled()).map(
            item => (
              <li
                key={item.path}
                className={classnames({
                  active: isActive(item, currentPath),
                })}
              >
                <a href={item.path}>{item.label}</a>
              </li>
            ),
          )}
        </ul>
      </div>
    </nav>
  );
}

CmsAdminNav.propTypes = {};
